import { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
} from '@mui/material';
import { Close as CloseIcon, Undo as UndoIcon, Delete as DeleteIcon } from '@mui/icons-material';

interface Redaction {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface RedactionModalProps {
  open: boolean;
  onClose: () => void;
  screenshot: string | null;
  onSave: (redactedImage: string) => void;
}

export default function RedactionModal({ open, onClose, screenshot, onSave }: RedactionModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [redactions, setRedactions] = useState<Redaction[]>([]);
  const [currentRedaction, setCurrentRedaction] = useState<Redaction | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (open && screenshot) {
      const img = new Image();
      img.onload = () => {
        imageRef.current = img;
        setImageLoaded(true);
        drawCanvas();
      };
      img.src = screenshot;
    } else {
      setImageLoaded(false);
      setRedactions([]);
      setCurrentRedaction(null);
    }
  }, [open, screenshot]);

  useEffect(() => {
    if (imageLoaded) {
      drawCanvas();
    }
  }, [redactions, currentRedaction, imageLoaded]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    if (!ctx || !img) return;

    canvas.width = img.width;
    canvas.height = img.height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);

    [...redactions, currentRedaction].filter(Boolean).forEach((rect) => {
      if (rect) {
        ctx.fillStyle = '#000000';
        ctx.fillRect(rect.x, rect.y, rect.width, rect.height);

        ctx.strokeStyle = '#ff0000';
        ctx.lineWidth = 2;
        ctx.strokeRect(rect.x, rect.y, rect.width, rect.height);
      }
    });
  };

  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoordinates(e);
    setIsDrawing(true);
    setCurrentRedaction({
      x: coords.x,
      y: coords.y,
      width: 0,
      height: 0,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !currentRedaction) return;

    const coords = getCanvasCoordinates(e);
    setCurrentRedaction({
      ...currentRedaction,
      width: coords.x - currentRedaction.x,
      height: coords.y - currentRedaction.y,
    });
  };

  const handleMouseUp = () => {
    if (currentRedaction && (Math.abs(currentRedaction.width) > 5 || Math.abs(currentRedaction.height) > 5)) {
      setRedactions([...redactions, currentRedaction]);
    }
    setIsDrawing(false);
    setCurrentRedaction(null);
  };

  const handleUndo = () => {
    setRedactions(redactions.slice(0, -1));
  };

  const handleClearAll = () => {
    setRedactions([]);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const redactedImage = canvas.toDataURL('image/png');
    onSave(redactedImage);
    onClose();
  };

  const handleCancel = () => {
    setRedactions([]);
    setCurrentRedaction(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          height: '95vh',
          maxHeight: '95vh',
          m: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Redact Sensitive Information</Typography>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Click and drag to draw black rectangles over sensitive information
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            overflow: 'auto',
            p: 2,
          }}
        >
          {imageLoaded && (
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                cursor: 'crosshair',
                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
              }}
            />
          )}
        </Box>

        <Paper
          elevation={3}
          sx={{
            p: 2,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 0,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Redactions: {redactions.length}
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<UndoIcon />}
              onClick={handleUndo}
              disabled={redactions.length === 0}
              size="small"
            >
              Undo Last
            </Button>
            <Button
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={handleClearAll}
              disabled={redactions.length === 0}
              size="small"
              color="error"
            >
              Clear All
            </Button>
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained">
          Apply Redactions
        </Button>
      </DialogActions>
    </Dialog>
  );
}
